import { notification } from 'components/Notification/notification';
import { BaseResponseModel, ApiException } from 'services/tenantManagementService';
import { clearStorage } from 'utils/storageUtils';
import { ErrorsType, globalErrorKey } from 'components/Form/models';

export const defaultErrorResponse = new BaseResponseModel({
	success: false,
	errors: {}
})

const fixErrorResponse = (result: any) => ({ ...result, success: false })

export const catchError = <P extends BaseResponseModel>(error: any) => {
	// server error
	if (error.status === 500) {
		notification.error({
			message: 'i18n.base.fetch.notification.unknownServerError'
		})
		return defaultErrorResponse as P;
	}

	// validation error
	if (error.status === 400) {
		notification.error({
			message: 'i18n.base.fetch.notification.validationError'
		})
		// error.result is BaseResponseModel all the time (if backend is correctly implemented)
		return fixErrorResponse((error as ApiException).result);
	}

	// forbidden, there is no permission for this user or action is not possible (example: cannot delete entity that is used)
	if (error.status === 403) {
		// error.result is BaseResponseModel all the time (if backend is correctly implemented)
		const result = (error as ApiException).result;
		const errors = (result as BaseResponseModel)?.errors;
		const message = errors && errors[globalErrorKey] && errors[globalErrorKey].message;
		notification.error({
			message: message || 'i18n.base.fetch.notification.permissionError'
		})
		return fixErrorResponse(result);
	}

	// unauthorized error
	if (error.status === 401) {
		// redirect to login
		clearStorage();
		// when token is invalid, storage is cleared and we reload the page
		// when page is loaded again, PageRouter inside of renderHomePage will save location.pathname to router state and redirect to Login
		window.location.reload();

		notification.error({
			message: 'i18n.base.fetch.notification.unauthorizedError'
		})

		return defaultErrorResponse as P;
	}

	if (error instanceof TypeError) {
		// INFO: this is not useful to user, it is more for us to see the message
		notification.error({
			message: error.toString()
		})
		return defaultErrorResponse as P;
	}

	notification.error({
		message: 'i18n.base.fetch.notification.unknownError'
	})
	return defaultErrorResponse as P;
}

export const tryCatchJsonByAction = async <P extends BaseResponseModel>(fetchFunction: (...args: any[]) => Promise<P>): Promise<P> => {
	const fetchPromise = fetchFunction();

	try {
		const response = await fetchPromise;
		return response || defaultErrorResponse as P;
	}
	catch (error: any) {
		return catchError<P>(error);
	}
}

// INFO: convertResponseErrors should be called in tryCatchJson,
// and all calls to this method should be removed from containers

// response.errors can contain next strings as fields:
// - 'topicsDelta.insert[0].description': MessageModel
// - 'someDelta.insert[0]': MessageModel
// - 'someField': MessageModel
// and we need to convert those strings to a ErrorsType structure, and finaly to map MessageModel to ErrorType
export const convertResponseErrors = (response: BaseResponseModel): ErrorsType | undefined => {
	if (response.errors) {
		const errorsMap: ErrorsType = {};
		for (const key of Object.keys(response.errors)) {
			key.split('.').reduce(
				(accumulator: any, currentSubKey: string, index: number, array: string[]) => {
					let arrayRegex = /^[a-zA-Z]+[[0-9]*]/g;
					let newObj: any;

					// check is property name array "insert[0]"
					if (arrayRegex.test(currentSubKey)) {
						const splited = currentSubKey.split('[');
						const arrayKey = splited[0];
						const arrayIndex = parseInt(splited[1].slice(0, -1));

						//create array first, then create object
						newObj = accumulator[arrayKey] || [];
						accumulator[arrayKey] = newObj;

						newObj = accumulator[arrayKey][arrayIndex] || {};

						// if last, set message
						if (index === array.length - 1) {
							newObj = response.errors![key].message ? {
								message: response.errors![key].message,
								i18nArgs: response.errors![key].additionalInfo
							} : undefined;
						}
						// set accumulator to new object
						accumulator[arrayKey][arrayIndex] = newObj;
					} else { // property name is object
						newObj = accumulator[currentSubKey] || {};
						if (index === array.length - 1) {
							// if last, set message
							newObj = response.errors![key].message ? {
								message: response.errors![key].message,
								i18nArgs: response.errors![key].additionalInfo
							} : undefined
						}
						// set accumulator to new object
						accumulator[currentSubKey] = newObj;
					}

					return newObj;
				},
				errorsMap
			);
		}
		return errorsMap;
	}
}
